import { notifySuccess } from '@/store/useToastStore';
import { useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Plus } from 'lucide-react';
import { database, txn } from '@/db';
import { Promotion } from '@/db/models/System';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { formatCurrency } from '@/utils/format';

export function PromotionsScreen() {
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const [open, setOpen] = useState(false);
  const promotions = useObservable<Promotion[]>(
    () => merchantId
      ? database.get<Promotion>('promotions').query(Q.where('merchant_id', merchantId), Q.where('is_deleted', false), Q.sortBy('created_at', Q.desc)).observe()
      : null,
    [merchantId],
    [],
  );
  if (!merchantId) return <div className="text-body text-fg-secondary">No merchant context.</div>;
  return (
    <div className="flex h-full flex-col gap-5">
      <div className="flex items-center justify-between">
        <div><h1 className="text-display text-fg-primary">Promotions</h1><p className="mt-1 text-body text-fg-secondary">Coupon codes available in billing.</p></div>
        <Button onClick={() => setOpen(true)}><Plus size={16} /> New promotion</Button>
      </div>
      <div className="flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
        {promotions.map((promo) => (
          <div key={promo.id} className="flex items-center justify-between border-b border-stroke-hairline/5 px-4 py-3">
            <div><div className="font-medium text-fg-primary">{promo.name}</div><div className="text-label uppercase text-fg-tertiary">{promo.code || 'NO CODE'} · {promo.type} · used {promo.usedCount}{promo.usageLimit ? `/${promo.usageLimit}` : ''}</div></div>
            <div className="text-right"><div className="font-medium">{promo.type === 'PERCENTAGE' ? `${promo.value}%` : formatCurrency(promo.value)}</div><div className={promo.isActive ? 'text-label text-success-ink' : 'text-label text-warning-ink'}>{promo.isActive ? 'Active' : 'Inactive'}</div></div>
          </div>
        ))}
        {promotions.length === 0 && <div className="p-8 text-center text-body text-fg-tertiary">No promotions yet.</div>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title="New promotion">
          <PromotionForm merchantId={merchantId} onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PromotionForm({ merchantId, onDone }: { merchantId: string; onDone: () => void }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [type, setType] = useState<'PERCENTAGE' | 'FIXED_AMOUNT'>('PERCENTAGE');
  const [value, setValue] = useState('');
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  async function save() {
    if (!name.trim() || !code.trim() || !(Number(value) > 0)) return;
    await txn(async (db) => {
      await db.get<Promotion>('promotions').create((record) => {
        record.merchantId = merchantId;
        record.name = name.trim();
        record.code = code.trim().toUpperCase();
        record.type = type;
        record.value = Number(value);
        record.minOrderValue = min ? Number(min) : null;
        record.maxDiscount = max ? Number(max) : null;
        record.applicableTo = 'ALL';
        record.applicableIds = null;
        record.startDate = new Date();
        record.endDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
        record.usageLimit = null;
        record.usedCount = 0;
        record.isActive = true;
        record.version = 1;
        record.isSynced = false;
        record.isDeleted = false;
        record.lastSyncUsedCount = null;
      });
    });
    notifySuccess(`Promotion “${name.trim()}” created.`);
    onDone();
  }
  return (
    <div className="space-y-4 p-5">
      <div className="grid grid-cols-2 gap-3">
        <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Name</span><Input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></label>
        <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Code</span><Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} /></label>
        <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Type</span><select value={type} onChange={(e) => setType(e.target.value as typeof type)} className="h-9 w-full rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 text-body"><option value="PERCENTAGE">Percentage</option><option value="FIXED_AMOUNT">Fixed amount</option></select></label>
        <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Value</span><Input type="number" value={value} onChange={(e) => setValue(e.target.value)} /></label>
        <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Min order</span><Input type="number" value={min} onChange={(e) => setMin(e.target.value)} /></label>
        <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Max discount</span><Input type="number" value={max} onChange={(e) => setMax(e.target.value)} /></label>
      </div>
      <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onDone}>Cancel</Button><Button onClick={() => void save()}>Save promotion</Button></div>
    </div>
  );
}
